import {useFeatures} from '@/hooks/useFeatures/useFeatures';
import {LayoutSwitch} from '@/layout/LayoutSwitch';
import {SentryRoutes} from '@/main';
import {useIsMobile} from '@ark/web';
import React, {useEffect} from 'react';
import lazyWithPreload from 'react-lazy-with-preload';
import {Route} from 'react-router-dom';
import {importWithRetry, preloadAll} from '@/utils/chunkReloadOperations';

const MemberListPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/Admin/Members/List/MemberListPage').then(module => ({
      default: module.MemberListPage,
    })),
  ),
);
const NewMemberPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/Admin/Members/NewMemberPage/NewMemberPage').then(
      module => ({
        default: module.NewMemberPage,
      }),
    ),
  ),
);
const ViewMemberPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/Admin/Members/ViewMemberPage/ViewMemberPage').then(
      module => ({
        default: module.ViewMemberPage,
      }),
    ),
  ),
);
const ProfilePage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/ProfilePage/ProfilePage').then(module => ({
      default: module.ProfilePage,
    })),
  ),
);
const Settings = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/Admin/Settings/Settings').then(module => ({
      default: module.Settings,
    })),
  ),
);
const PageNotFound = lazyWithPreload(() =>
  importWithRetry(() => import('../../pages/ENT/404Page')),
);
const ViewTeamPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/Admin/Teams/Tabs/ViewTeamPage').then(module => ({
      default: module.ViewTeamPage,
    })),
  ),
);

const TeamListPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/Admin/Teams/List/TeamListPage').then(module => ({
      default: module.TeamListPage,
    })),
  ),
);
const NewTeamPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/Admin/Teams/NewTeamPage/NewTeamPage').then(module => ({
      default: module.NewTeamPage,
    })),
  ),
);
const TagListPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/Admin/Tags/List/TagListPage').then(module => ({
      default: module.TagListPage,
    })),
  ),
);

const ViewTagPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/Admin/Tags/Tabs/ViewTagPage').then(module => ({
      default: module.ViewTagPage,
    })),
  ),
);

const DashboardPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/Dashboard/DashboardPage/DashboardPage').then(
      module => ({
        default: module.DashboardPage,
      }),
    ),
  ),
);

const NotificationListPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/Notifications/List/NotificationListPage').then(
      module => ({
        default: module.NotificationListPage,
      }),
    ),
  ),
);

const PulsePage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/Pulse/List/PulsePage').then(module => ({
      default: module.PulsePage,
    })),
  ),
);

const RulesPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/Rules/List/RulesPage').then(module => ({
      default: module.RulesPage,
    })),
  ),
);

const ViewRulePage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/Rules/Tabs/ViewRulePage').then(module => ({
      default: module.ViewRulePage,
    })),
  ),
);

const NewRulePage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/Rules/NewRulePage/NewRulePage').then(module => ({
      default: module.NewRulePage,
    })),
  ),
);

const SiteReportingPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/Reporting/SiteReporting/SiteReportingPage').then(
      module => ({
        default: module.SiteReportingPage,
      }),
    ),
  ),
);

const PortfolioReportingPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/Reporting/Portfolio/PortfolioReportingPage').then(
      module => ({
        default: module.PortfolioReportingPage,
      }),
    ),
  ),
);

const TeamReportingPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/Reporting/TeamReporting/TeamReportingPage').then(
      module => ({
        default: module.TeamReportingPage,
      }),
    ),
  ),
);

const ConversationsListPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/Conversations/List/ConversationsPage').then(
      module => ({
        default: module.ConversationsPage,
      }),
    ),
  ),
);

const SavedViewsPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/Reporting/SavedViews/SavedViewsPage').then(
      module => ({
        default: module.SavedViewsPage,
      }),
    ),
  ),
);

const ConversationsPageV2Style = lazyWithPreload(() =>
  importWithRetry(() =>
    import(
      '../../pages/ENT/Conversations/ConversationPageV2Style/ConversationsPage'
    ).then(module => ({
      default: module.ConversationsPage,
    })),
  ),
);

const SavedViewPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('../../pages/ENT/Reporting/SavedViews/SavedViewPage').then(
      module => ({
        default: module.SavedViewPage,
      }),
    ),
  ),
);

const componentsToPreload = [
  MemberListPage,
  NewMemberPage,
  ViewMemberPage,
  ProfilePage,
  Settings,
  PageNotFound,
  ViewTeamPage,
  TeamListPage,
  TagListPage,
  ViewTagPage,
  DashboardPage,
  NewTeamPage,
  NotificationListPage,
  PulsePage,
  RulesPage,
  ViewRulePage,
  NewRulePage,
];

const AdminRoutes: React.FC = () => {
  useEffect(() => {
    preloadAll(componentsToPreload);
  }, []);

  const [features] = useFeatures();
  const isMobile = useIsMobile('md');
  const ConversationsPage =
    features.ConversationLayout && !isMobile
      ? ConversationsListPage
      : ConversationsPageV2Style;

  return (
    <SentryRoutes>
      <Route element={<LayoutSwitch />}>
        <Route path="/" element={<MemberListPage />} />
        <Route path="/members" element={<MemberListPage />} />
        <Route path="/members/new" element={<NewMemberPage />} />
        <Route path="/members/:id/:tab?" element={<ViewMemberPage />} />
        <Route path="/profile/:tab?" element={<ProfilePage />} />
        <Route path="/settings/:id?" element={<Settings />} />
        {features.Teams && (
          <Route path="/teams/new" element={<NewTeamPage />} />
        )}
        <Route path="/teams/:teamId/:tab?" element={<ViewTeamPage />} />
        <Route path="/teams" element={<TeamListPage />} />
        <Route path="/tags" element={<TagListPage />} />
        <Route path="/tags/:id/:tab?" element={<ViewTagPage />} />
        <Route path="/dashboard" element={<DashboardPage />} />
        {features.Reporting && (
          <>
            <Route
              path="/reporting/site/:communityId?/:tab?"
              element={<SiteReportingPage />}
            />
            <Route path="/reporting/saved" element={<SavedViewsPage />} />
            <Route path="/reporting/saved/:id" element={<SavedViewPage />} />
          </>
        )}
        {features.Reporting && features.PortfolioReporting && (
          <Route
            path="/reporting/portfolio"
            element={<PortfolioReportingPage />}
          />
        )}
        {features.Reporting && features.TeamReporting && (
          <Route path="/reporting/team" element={<TeamReportingPage />} />
        )}
        <Route
          path="/messages/:conversationId?"
          element={<ConversationsPage />}
        />
        <Route path="/notifications" element={<NotificationListPage />} />
        <Route path="/pulse" element={<PulsePage />} />
        {features.Rules && (
          <>
            <Route path="/rules" element={<RulesPage />} />
            <Route path="/rules/new" element={<NewRulePage />} />
            <Route path="/rules/:ruleId/:tab?" element={<ViewRulePage />} />
          </>
        )}
        <Route path="*" element={<PageNotFound />} />
      </Route>
    </SentryRoutes>
  );
};

export default AdminRoutes;
